import { LStorage } from '@shared/Storage';
import { cs } from '@shared/utils/utils-classname';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import './FloatingWindow.scss';

/*
 * FloatingWindow component is using react-rnd which doesn't support anchoring to right edge
 * so we use custom implementation, check later if react-rnd added it.
 * We need right anchor so that dynamic window content would grow to the left side.
 */
@observer
export class FloatingWindowSimpleComponent extends React.Component<{
    anchor: 'left' | 'right';
    children: React.ReactNode;
    limitToBounds?: boolean;
    defaultX?: number;
    defaultY?: number;
    storageId?: string;
    boundTop?: number;
    boundLeft?: number;
    onRightDrag?: (delta: number) => void;
    onRightDragEnd?: (delta: number) => void;
    animate?: boolean;
}> {
    @observable translateX = this.props.defaultX ?? 100;
    @observable translateY = this.props.defaultY ?? 100;
    @observable maxHeight = window.innerHeight;
    isDragging = false;
    dragStartX = 0;
    dragStartY = 0;
    dragStartTranslateX = 0;
    dragStartTranslateY = 0;
    rightDragMouseDelta = 0;
    containerRef: HTMLDivElement = null;

    constructor(props) {
        super(props);
        makeObservable(this);
        this.loadPosition();
    }

    componentDidMount() {
        this.checkBounds();
        this.updateMaxHeight();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.boundTop !== this.props.boundTop || prevProps.boundLeft !== this.props.boundLeft) {
            this.checkBounds();
        }
    }

    loadPosition = () => {
        if (!this.props.storageId) return;

        const stored = LStorage.getOrDefault<{ x?: number; y?: number }>(`floating-window-${this.props.storageId}`, {});
        if (!stored || typeof stored.x !== 'number') return;

        this.translateX = stored.x;
        this.translateY = typeof stored.y === 'number' ? stored.y : this.translateY;
        this.checkBounds();
    };

    savePosition = () => {
        if (!this.props.storageId) return;

        LStorage.setItem(`floating-window-${this.props.storageId}`, { x: this.translateX, y: this.translateY });
    };

    getBoundLeft = () => {
        return Number.isFinite(this.props.boundLeft) ? this.props.boundLeft : 0;
    };

    getBoundTop = () => {
        return Number.isFinite(this.props.boundTop) ? this.props.boundTop : 0;
    };

    checkBounds = () => {
        if (!this.containerRef) return;

        const rect = this.containerRef.getBoundingClientRect();
        const boundLeft = this.getBoundLeft();
        const minX = this.props.anchor === 'left' ? boundLeft : 0;
        const maxX = this.props.anchor === 'left' ? window.innerWidth - rect.width : window.innerWidth - rect.width - boundLeft;
        const minY = this.getBoundTop();
        const maxY = window.innerHeight - rect.height;

        this.translateX = utils.clamp(minX, this.translateX, Math.max(minX, maxX));
        this.translateY = utils.clamp(minY, this.translateY, Math.max(minY, maxY));
        this.updateMaxHeight();
    };

    updateMaxHeight = () => {
        this.maxHeight = window.innerHeight - this.translateY;
    };

    handleMouseDown = (e: React.MouseEvent) => {
        if (!(e.target as HTMLElement).closest('.drag-area')) return;

        e.preventDefault();
        this.isDragging = true;
        this.dragStartX = e.clientX;
        this.dragStartY = e.clientY;
        this.dragStartTranslateX = this.translateX;
        this.dragStartTranslateY = this.translateY;

        document.addEventListener('mousemove', this.handleMouseMove);
        document.addEventListener('mouseup', this.handleMouseUp);
    };

    handleMouseMove = (e: MouseEvent) => {
        if (!this.isDragging) return;

        const deltaX = e.clientX - this.dragStartX;
        const deltaY = e.clientY - this.dragStartY;

        let newX = this.dragStartTranslateX + (this.props.anchor === 'right' ? -deltaX : deltaX);
        let newY = this.dragStartTranslateY + deltaY;

        if (this.containerRef) {
            const rect = this.containerRef.getBoundingClientRect();
            const boundOffset = this.getBoundLeft();
            const minX = this.props.anchor === 'left' ? boundOffset : 0;
            const maxX = this.props.anchor === 'left' ? window.innerWidth - rect.width : window.innerWidth - rect.width - boundOffset;
            const minY = this.getBoundTop();
            const maxY = window.innerHeight - rect.height;

            newX = utils.clamp(minX, newX, Math.max(minX, maxX));
            newY = utils.clamp(minY, newY, Math.max(minY, maxY));
        }

        this.translateX = newX;
        this.translateY = newY;
        this.updateMaxHeight();
    };

    handleRightDragHandleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        this.dragStartX = e.clientX;
        this.dragStartTranslateX = this.translateX;
        this.rightDragMouseDelta = 0;

        document.addEventListener('mousemove', this.handleRightDragHandleMouseMove);
        document.addEventListener('mouseup', this.handleRightDragHandleMouseUp);
    };

    handleRightDragHandleMouseMove = (e: MouseEvent) => {
        const mouseDelta = e.clientX - this.dragStartX;
        const deltaX = Math.max(0, mouseDelta);
        this.rightDragMouseDelta = mouseDelta;
        let newX = this.dragStartTranslateX + (this.props.anchor === 'right' ? -deltaX : deltaX);

        if (this.containerRef) {
            const rect = this.containerRef.getBoundingClientRect();
            const boundOffset = this.getBoundLeft();
            const minX = this.props.anchor === 'left' ? boundOffset : 0;
            const maxX = this.props.anchor === 'left' ? window.innerWidth - rect.width : window.innerWidth - rect.width - boundOffset;

            newX = utils.clamp(minX, newX, Math.max(minX, maxX));
        }

        this.translateX = newX;
        this.triggerRightDrag(this.getRightDragDelta(deltaX));
    };

    triggerRightDrag = _.throttle((delta: number) => {
        if (!this.props.onRightDrag) return;

        this.props.onRightDrag(delta);
    }, 30);

    getRightDragDelta = (mouseDelta = this.rightDragMouseDelta) => {
        const delta = this.props.anchor === 'right' ? this.dragStartTranslateX - this.translateX : this.translateX - this.dragStartTranslateX;
        if (delta > 0) return delta;
        if (mouseDelta !== 0) return -Math.abs(mouseDelta);
        return 0;
    };

    handleRightDragHandleMouseUp = (e: MouseEvent) => {
        document.removeEventListener('mousemove', this.handleRightDragHandleMouseMove);
        document.removeEventListener('mouseup', this.handleRightDragHandleMouseUp);
        this.rightDragMouseDelta = e.clientX - this.dragStartX;
        if (this.props.onRightDragEnd) {
            this.props.onRightDragEnd(this.getRightDragDelta());
        }
        this.savePosition();
    };

    handleMouseUp = () => {
        this.isDragging = false;
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
        this.savePosition();
    };

    componentWillUnmount() {
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
        document.removeEventListener('mousemove', this.handleRightDragHandleMouseMove);
        document.removeEventListener('mouseup', this.handleRightDragHandleMouseUp);
    }

    render() {
        const anchorStyle = this.props.anchor === 'left' ? { left: 0 } : { right: 0 };
        const transformX = this.props.anchor === 'left' ? this.translateX : -this.translateX;

        return (
            <div
                ref={el => (this.containerRef = el)}
                className={cs('floating-window-simple-container', { animate: this.props.animate })}
                style={{
                    position: 'fixed',
                    top: 0,
                    ...anchorStyle,
                    transform: `translate(${transformX}px, ${this.translateY}px)`,
                }}
                onMouseDown={this.handleMouseDown}
            >
                {this.props.onRightDrag && <div className='floating-window-right-drag-handle resizeHandle-style' onMouseDown={this.handleRightDragHandleMouseDown}></div>}
                <div
                    className={cs('card floating-window-body')}
                    style={{
                        height: 'auto',
                        maxHeight: this.maxHeight,
                    }}
                >
                    <div className='floating-window-header'>
                        <div className='drag-area' style={{ cursor: 'grab' }}>
                            <div className='drag-handle'></div>
                        </div>
                    </div>
                    {this.props.children}
                </div>
            </div>
        );
    }
}
